// Nation calendar basis for Abaryt Year 5926.
// Day 1 began 17 March 2026 at sundown in the confirmed northeastern
// Africa–Turkey witness region. The year remains anchored to that witness,
// while each visitor's current Abaryt day changes at their local sundown.
const DAY = 86400000;
const YEAR_START_DAYLIGHT = Date.UTC(2026, 2, 18);
const MONTH_LENGTHS = [30, 30, 31, 30, 30, 31, 30, 30, 31, 30, 30, 31];

// Fixed northeastern Africa reference near the Nile delta. The year itself
// remains anchored to the Nation's confirmed spring witness.
export const NATION_REFERENCE = {
  latitude: 30.0444,
  longitude: 31.2357,
  label: "Africa/Türkiye reference",
};

export const NATION_REFERENCE_TIME_ZONE = "Africa/Cairo";

const TIME_ZONE_REFERENCE_POINTS = {
  "America/Jamaica": [17.96806, -76.79333],
  "America/New_York": [40.71417, -74.00639],
  "America/Chicago": [41.85, -87.65],
  "America/Denver": [39.73917, -104.98417],
  "America/Los_Angeles": [34.05222, -118.24278],
  "America/Phoenix": [33.44833, -112.07333],
  "America/Anchorage": [61.21806, -149.90028],
  "America/Toronto": [43.65, -79.38333],
  "America/Vancouver": [49.26667, -123.11667],
  "America/Mexico_City": [19.4, -99.15],
  "America/Puerto_Rico": [18.46833, -66.10611],
  "America/Panama": [8.96667, -79.53333],
  "America/Bogota": [4.6, -74.08333],
  "America/Lima": [-12.05, -77.05],
  "America/Sao_Paulo": [-23.53333, -46.61667],
  "America/Argentina/Buenos_Aires": [-34.6, -58.45],
  "Europe/London": [51.50833, -0.12528],
  "Europe/Paris": [48.86667, 2.33333],
  "Europe/Berlin": [52.5, 13.36667],
  "Europe/Istanbul": [41.01667, 28.96667],
  "Europe/Moscow": [55.75583, 37.61778],
  "Africa/Cairo": [30.05, 31.25],
  "Africa/Nairobi": [-1.28333, 36.81667],
  "Africa/Johannesburg": [-26.25, 28.0],
  "Africa/Lagos": [6.45, 3.4],
  "Asia/Jerusalem": [31.78056, 35.22389],
  "Asia/Dubai": [25.3, 55.3],
  "Asia/Kolkata": [22.53333, 88.36667],
  "Asia/Bangkok": [13.75, 100.51667],
  "Asia/Shanghai": [31.23333, 121.46667],
  "Asia/Tokyo": [35.65444, 139.74472],
  "Australia/Sydney": [-33.86667, 151.21667],
  "Australia/Perth": [-31.95, 115.85],
  "Pacific/Auckland": [-36.86667, 174.76667],
  "Pacific/Honolulu": [21.30694, -157.85833],
};

const REGION_LATITUDE_FALLBACK = {
  America: 30,
  Europe: 50,
  Africa: 10,
  Asia: 30,
  Australia: -30,
  Pacific: 0,
  Indian: -10,
  Atlantic: 25,
};

export function getTimeZoneReference(timeZone, now = new Date()) {
  if (!timeZone || typeof timeZone !== "string") return null;

  const direct = TIME_ZONE_REFERENCE_POINTS[timeZone];
  if (direct) {
    return {
      latitude: direct[0],
      longitude: direct[1],
      label: `Approximate ${timeZone} reference`,
      timeZone,
      approximate: true,
    };
  }

  const region = timeZone.split("/")[0];
  const latitude = REGION_LATITUDE_FALLBACK[region];
  if (!Number.isFinite(latitude)) return null;

  // The browser's current UTC offset gives a reasonable central longitude for
  // an unmapped IANA zone. This is deliberately approximate, not precise location.
  const offsetHours = -now.getTimezoneOffset() / 60;
  const longitude = Math.max(-180, Math.min(180, offsetHours * 15));

  return {
    latitude,
    longitude,
    label: `Approximate ${timeZone} reference`,
    timeZone,
    approximate: true,
  };
}

const APPOINTED_DAYS = {
  "01-01": "First-month remembrance",
  "01-14": "Pasach",
  "01-15": "Chag Matzah",
  "01-26": "First Fruits",
  "03-15": "Shabu'ut",
  "04-01": "Fourth-month remembrance",
  "07-01": "Tzakarun Taruah",
  "07-10": "Yum Ha'Kapurym",
  "07-15": "Sakut",
  "07-22": "Closing Festival",
  "10-01": "Tenth-month remembrance",
};

export const FEAST_DAYS = [
  { month: 1, day: 14, label: "Pasach" },
  { month: 1, day: 15, label: "Chag Matzah" },
  { month: 1, day: 26, label: "First Fruits" },
  { month: 3, day: 15, label: "Shabu'ut" },
  { month: 7, day: 1, label: "Tzakarun Taruah" },
  { month: 7, day: 10, label: "Yum Ha'Kapurym" },
  { month: 7, day: 15, label: "Sakut" },
  { month: 7, day: 22, label: "Closing Festival" },
];

const dayOfYearFor = (month, day) =>
  MONTH_LENGTHS.slice(0, month - 1).reduce((total, length) => total + length, 0) + day;

export function getNextFeast(abarytDate) {
  if (!abarytDate?.dayOfYear) return null;
  const current = Number(abarytDate.dayOfYear);
  const next = FEAST_DAYS
    .map((feast) => ({ ...feast, dayOfYear: dayOfYearFor(feast.month, feast.day) }))
    .find((feast) => feast.dayOfYear >= current);

  if (!next) return null;

  return {
    ...next,
    daysUntil: next.dayOfYear - current,
  };
}

function civilDateParts(now, timeZone, longitude) {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(now);
      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      return {
        year: Number(values.year),
        month: Number(values.month),
        day: Number(values.day),
      };
    } catch {
      // Fall through to mean-solar date.
    }
  }

  const solarDate = new Date(now.getTime() + longitude * 240000);
  return {
    year: solarDate.getUTCFullYear(),
    month: solarDate.getUTCMonth() + 1,
    day: solarDate.getUTCDate(),
  };
}

function gregorianLabelFor(year, month, day) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

const radians = (degrees) => degrees * Math.PI / 180;
const normalize = (degrees) => ((degrees % 360) + 360) % 360;

export function sunsetUtc(year, month, day, latitude, longitude) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)
    || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;

  const n = Math.floor((Date.UTC(year, month - 1, day) - Date.UTC(year, 0, 1)) / DAY) + 1;
  const longitudeHour = longitude / 15;
  const t = n + (18 - longitudeHour) / 24;
  const anomaly = 0.9856 * t - 3.289;
  const trueLongitude = normalize(anomaly + 1.916 * Math.sin(radians(anomaly))
    + 0.020 * Math.sin(radians(2 * anomaly)) + 282.634);
  let rightAscension = normalize(Math.atan(0.91764 * Math.tan(radians(trueLongitude))) * 180 / Math.PI);
  rightAscension += Math.floor(trueLongitude / 90) * 90 - Math.floor(rightAscension / 90) * 90;
  const sinDeclination = 0.39782 * Math.sin(radians(trueLongitude));
  const cosDeclination = Math.cos(Math.asin(sinDeclination));
  const cosine = (Math.cos(radians(90.833)) - sinDeclination * Math.sin(radians(latitude)))
    / (cosDeclination * Math.cos(radians(latitude)));
  if (cosine < -1 || cosine > 1) return null;
  const hourAngle = Math.acos(cosine) * 180 / Math.PI / 15;
  const localMeanTime = hourAngle + rightAscension / 15 - 0.06571 * t - 6.622;
  const localHour = ((localMeanTime % 24) + 24) % 24;
  return new Date(Date.UTC(year, month - 1, day) + (localHour - longitudeHour) * 3600000);
}

export function getAbarytDate(now = new Date(), position = NATION_REFERENCE, timeZone = null) {
  const candidate = position || NATION_REFERENCE;
  const latitude = Number(candidate.latitude);
  const longitude = Number(candidate.longitude);
  const hasValidPosition = Number.isFinite(latitude) && Number.isFinite(longitude)
    && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
  const resolved = hasValidPosition ? { latitude, longitude } : NATION_REFERENCE;

  const civil = civilDateParts(now, timeZone, resolved.longitude);
  const { year, month, day } = civil;
  const sunset = sunsetUtc(year, month, day, resolved.latitude, resolved.longitude);
  if (!sunset) return null;

  const gregorianLabel = gregorianLabelFor(year, month, day);

  const dayIndex = Math.round((Date.UTC(year, month - 1, day) - YEAR_START_DAYLIGHT) / DAY)
    + (now >= sunset ? 1 : 0);

  if (dayIndex < 0 || dayIndex >= 364) return null;

  let remainder = dayIndex;
  const monthIndex = MONTH_LENGTHS.findIndex((length) => {
    if (remainder < length) return true;
    remainder -= length;
    return false;
  });

  const abarytMonth = monthIndex + 1;
  const abarytDay = remainder + 1;
  const dateKey = `${String(abarytMonth).padStart(2, "0")}-${String(abarytDay).padStart(2, "0")}`;
  const appointedTime = APPOINTED_DAYS[dateKey] || "";

  return {
    year: 5926,
    month: abarytMonth,
    day: abarytDay,
    dayOfYear: dayIndex + 1,
    appointedTime,
    gregorianLabel,
    referenceSundown: sunset,
    label: `${monthIndex === 5 ? "SHaSHY · " : ""}Month ${abarytMonth} · Day ${abarytDay} · Year 5926`,
  };
}
